import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { DEPOT, cents, formatUsd, type LatLng } from "@moomoo/core";
import { api, type Quote } from "../lib/api.js";
import type { Translate } from "../lib/i18n.js";
import { LocateIcon, PinIcon } from "./bits.js";

/**
 * The Grab gesture: the pin stays fixed in the centre and the customer moves
 * the map under it. Dragging a marker on a phone is fiddly; dragging the map
 * is what every ride app taught people.
 *
 * Opening with no pin asks the phone for its location straight away, which
 * is where the customer usually is. The street under the pin is looked up
 * after the map settles, so they can see it landed on the right road.
 *
 * The map is created once and never re-rendered by React; only the confirmed
 * pin and the quote flow back through props.
 */

/** Central Phnom Penh, where the map opens when GPS is unavailable. */
const FALLBACK_CENTRE: LatLng = { lat: 11.5564, lng: 104.9282 };
const LABEL_DEBOUNCE_MS = 600;

export function LocationPicker({
  value,
  quote,
  quoteError,
  quoting,
  t,
  onChange,
}: {
  value: LatLng | undefined;
  quote: Quote | undefined;
  quoteError: string | undefined;
  quoting: boolean;
  t: Translate;
  onChange: (pin: LatLng | undefined, label: string | null) => void;
}) {
  const holder = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const [centre, setCentre] = useState<LatLng>(value ?? FALLBACK_CENTRE);
  const [editing, setEditing] = useState(!value);
  const [locating, setLocating] = useState(false);
  const [gpsNote, setGpsNote] = useState<string | undefined>();
  const [label, setLabel] = useState<string | null>(null);
  const [confirmedLabel, setConfirmedLabel] = useState<string | null>(null);

  useEffect(() => {
    if (!editing || !holder.current || map.current) return;
    const m = L.map(holder.current, { zoomControl: false, attributionControl: true }).setView(
      [centre.lat, centre.lng],
      16,
    );
    L.control.zoom({ position: "bottomright" }).addTo(m);
    // Humanitarian style: lighter than the default OSM look and, unlike CARTO
    // since 2025, served without an API key
    L.tileLayer("https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png", {
      subdomains: "abc",
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, tiles <a href="https://www.hotosm.org/">HOT</a> / <a href="https://openstreetmap.fr/">OSM France</a>',
    }).addTo(m);
    L.circleMarker([DEPOT.lat, DEPOT.lng], { radius: 6, color: "#0d2a55", fillOpacity: 1 })
      .addTo(m)
      .bindTooltip(DEPOT.nameKh);
    m.on("moveend", () => {
      const c = m.getCenter();
      setCentre({ lat: c.lat, lng: c.lng });
    });
    map.current = m;
    // no pin yet: this is almost always the customer's own house, so ask
    if (!value) locate();
    return () => {
      // confirm can land mid drag-inertia; a pan animation finishing after
      // remove() touches a pane that no longer exists
      m.stop();
      m.remove();
      map.current = null;
    };
    // centre and value are read once at creation on purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  /** Street under the pin, after the map has settled for a moment. */
  useEffect(() => {
    if (!editing) return;
    let live = true;
    const timer = setTimeout(() => {
      api
        .place(centre)
        .then((l) => {
          if (live) setLabel(l);
        })
        .catch(() => {
          if (live) setLabel(null);
        });
    }, LABEL_DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [centre, editing]);

  function locate() {
    if (!("geolocation" in navigator)) {
      setGpsNote(t("gpsDenied"));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setLocating(false);
        setGpsNote(undefined);
        map.current?.setView([here.lat, here.lng], 17);
        setCentre(here);
      },
      () => {
        setLocating(false);
        setGpsNote(t("gpsDenied"));
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
  }

  function confirm() {
    const pin = { lat: Math.round(centre.lat * 1e6) / 1e6, lng: Math.round(centre.lng * 1e6) / 1e6 };
    setConfirmedLabel(label);
    onChange(pin, label);
    setEditing(false);
  }

  const quoteLine = (() => {
    if (quoting) return t("quoting");
    if (quoteError?.startsWith("outside:")) {
      const phone = quoteError.slice("outside:".length);
      return `${t("outsideArea")}${phone ? " " + t("callUs", phone) : ""}`;
    }
    if (quoteError) return t("quoteFailed");
    if (quote) {
      const fee = formatUsd(cents(quote.feeCents));
      const est = quote.source === "estimated" ? ` (${t("estimated")})` : "";
      return `≈ ${quote.km} km · ${fee}${est}`;
    }
    return undefined;
  })();

  return (
    <div className="panel picker">
      <div className="kv" style={{ padding: "0 0 8px" }}>
        <span>{t("whereTo")}</span>
        <b>{value && !editing ? t("pinSet") : ""}</b>
      </div>

      {editing ? (
        <>
          <div className="map-wrap">
            <div ref={holder} className="map" role="application" aria-label={t("dragMap")} />
            <div className="map-pin" aria-hidden="true" />
            <div className="map-label" aria-live="polite">
              {locating ? t("locating") : label ?? t("dragMap")}
            </div>
          </div>
          {gpsNote ? <div className="field-error">{gpsNote}</div> : null}
          <button type="button" className="btn quiet locate" onClick={locate} disabled={locating}>
            <LocateIcon />
            {locating ? t("locating") : t("useMyLocation")}
          </button>
          <div className="picker-actions">
            <button type="button" className="btn" onClick={confirm} disabled={locating}>
              {t("confirmPin")}
            </button>
          </div>
        </>
      ) : (
        <div className="picker-set">
          <p className="pinned">
            <PinIcon />
            <span>{confirmedLabel ?? t("pinSet")}</span>
          </p>
          <button type="button" className="btn quiet" onClick={() => setEditing(true)}>
            {t("changePin")}
          </button>
        </div>
      )}

      {value && quoteLine ? (
        <p className={quoteError ? "field-error" : "note"} style={{ marginTop: 10 }}>
          {quoteLine}
        </p>
      ) : null}
      <p className="note">{t("feeHow")}</p>
    </div>
  );
}
